/**
 * RBAC 權限矩陣 - Single Source of Truth
 * 
 * 這是整個系統權限管理的核心檔案
 * 定義了「角色 × 模組 × 操作」的完整權限矩陣
 * 
 * 權限設計原則：
 * - owner: 系統治理者（技術、法務、全域控制）
 * - super_admin: 營運管理者（金融簽核、行政管理）
 * - store_manager: 據點管理者（日常營運、部分金融處理）
 * 
 * @module Permissions
 */

import type { AdminRole } from './roles';

/**
 * 操作類型
 */
export type Action = 
  | 'view_menu'      // 可見 sidebar
  | 'view_list'      // 可進入列表
  | 'view_detail'    // 可查看詳細
  | 'create'         // 可新增
  | 'edit'           // 可編輯
  | 'delete'         // 可刪除
  | 'process'        // 可處理（日常操作）
  | 'approve'        // 可審核（需要上級）
  | 'finalize'       // 可最終簽核（金融最終確認）
  | 'override'       // 可覆寫（異常修正）
  | 'export'         // 可匯出
  | 'manage_system'  // 可管理系統設定
  | 'manage_legal';  // 可管理法務文件

/**
 * 模組類型
 */
export type Module =
  | 'dashboard'           // 控制台
  | 'orders'              // 訂單管理
  | 'customers'           // 客戶管理
  | 'equipment'           // 器材管理
  | 'equipment_items'     // 品項管理
  | 'categories'          // 類別管理
  | 'brands'              // 品牌管理
  | 'accessories'         // 配件管理
  | 'locations'           // 據點管理
  | 'vehicles'            // 車輛管理
  | 'urent'               // Urent 管理
  | 'urent_ledger'        // Urent 明細
  | 'urent_contracts'     // Urent 合約
  | 'prepaid'             // 儲值管理
  | 'prepaid_customers'   // 儲值客戶
  | 'prepaid_ledger'      // 儲值明細
  | 'prepaid_plans'       // 儲值方案
  | 'analytics'           // 業績報表
  | 'contracts'           // 合約管理
  | 'forms'               // 表單管理
  | 'cms'                 // CMS
  | 'admins'              // 管理員管理
  | 'attendance'          // 出勤管理
  | 'settings'            // 系統設定
  | 'google_calendar'     // Google Calendar
  | 'line_notify'         // LINE Notify
  | 'api_settings'        // API 設定
  | 'milestones'         // 里程碑管理
  | 'schedules'          // 排班管理
  | 'ai_agent'          // AI Agent 管理
  | 'service_cases'     // 案例影片管理
  | 'storefront_sort'   // 前台商品排序設定
  | 'legal_settings'    // 甲方資料設定
  | 'frontend_images'  // 前台圖片管理
  | 'system_governance' // 系統治理資訊（owner only）
  | 'muvi_bookings'    // MuVi Space 預約管理
  | 'muvi_content'     // MuVi Space 內容管理
  | 'muvi_partners';   // MuVi Space 合作夥伴管理

/**
 * 模組顯示名稱對照
 */
export const moduleDisplayNames: Record<Module, string> = {
  dashboard: '儀表板',
  orders: '訂單管理',
  customers: '客戶管理',
  equipment: '器材管理',
  equipment_items: '品項管理',
  categories: '分類管理',
  brands: '品牌管理',
  accessories: '配件管理',
  locations: '據點管理',
  vehicles: '車輛管理',
  urent: 'Urent 管理',
  urent_ledger: 'Urent 明細',
  urent_contracts: 'Urent 合約',
  prepaid: '儲值管理',
  prepaid_customers: '儲值客戶',
  prepaid_ledger: '儲值明細',
  prepaid_plans: '儲值方案',
  analytics: '業績報表',
  contracts: '合約管理',
  forms: '表單管理',
  cms: 'CMS',
  admins: '管理員管理',
  attendance: '出勤管理',
  settings: '系統設定',
  google_calendar: 'Google Calendar',
  line_notify: 'LINE Notify',
  milestones: '里程碑管理',
  schedules: '排班管理',
  ai_agent: 'AI Agent',
  service_cases: '案例影片',
  api_settings: 'API 設定',
  storefront_sort: '前台商品排序設定',
  legal_settings: '甲方資料設定',
  frontend_images: '前台圖片管理',
  system_governance: '系統治理資訊',
  muvi_bookings: 'MuVi Space 預約管理',
  muvi_content: 'MuVi Space 內容管理',
  muvi_partners: 'MuVi Space 合作夥伴管理',
};

/**
 * 完整權限矩陣
 * 
 * 格式：permissions[module][action] = [允許的角色列表]
 */
export const permissions: Record<Module, Partial<Record<Action, AdminRole[]>>> = {
  // ==================== 控制台 ====================
  dashboard: {
    view_menu: ['owner', 'super_admin', 'store_manager', 'staff', 'part_time'],
    view_list: ['owner', 'super_admin', 'store_manager', 'staff', 'part_time'],
    view_detail: ['owner', 'super_admin', 'store_manager', 'staff', 'part_time'],
    create: ['owner', 'super_admin', 'store_manager'], // 新增公告
    edit: ['owner', 'super_admin', 'store_manager'],   // 編輯公告
    delete: ['owner', 'super_admin', 'store_manager'], // 刪除公告
  },

  // ==================== 訂單管理 ====================
  orders: {
    view_menu: ['owner', 'super_admin', 'store_manager', 'staff', 'part_time'],
    view_list: ['owner', 'super_admin', 'store_manager', 'staff', 'part_time'],
    view_detail: ['owner', 'super_admin', 'store_manager', 'staff', 'part_time'],
    create: ['owner', 'super_admin', 'store_manager', 'staff', 'part_time'],
    edit: ['owner', 'super_admin', 'store_manager', 'staff'],
    delete: ['owner', 'super_admin', 'store_manager'],
    approve: ['owner', 'super_admin', 'store_manager'],
    export: ['owner', 'super_admin', 'store_manager'],
  },

  // ==================== 客戶管理 ====================
  customers: {
    view_menu: ['owner', 'super_admin', 'store_manager', 'staff', 'part_time'],
    view_list: ['owner', 'super_admin', 'store_manager', 'staff', 'part_time'],
    view_detail: ['owner', 'super_admin', 'store_manager', 'staff', 'part_time'],
    create: ['owner', 'super_admin', 'store_manager', 'staff'],
    edit: ['owner', 'super_admin', 'store_manager', 'staff'],
    delete: ['owner', 'super_admin', 'store_manager'],
    export: ['owner', 'super_admin', 'store_manager'],
  },

  // ==================== 器材管理 ====================
  equipment: {
    view_menu: ['owner', 'super_admin', 'store_manager', 'staff', 'part_time'],
    view_list: ['owner', 'super_admin', 'store_manager', 'staff', 'part_time'],
    view_detail: ['owner', 'super_admin', 'store_manager', 'staff', 'part_time'],
    create: ['owner', 'super_admin', 'store_manager', 'staff'],
    edit: ['owner', 'super_admin', 'store_manager', 'staff'],
    delete: ['owner', 'super_admin', 'store_manager'],
    export: ['owner', 'super_admin', 'store_manager'],
  },

  // ==================== 品項管理 ====================
  equipment_items: {
    view_menu: ['owner', 'super_admin', 'store_manager', 'staff', 'part_time'],
    view_list: ['owner', 'super_admin', 'store_manager', 'staff', 'part_time'],
    view_detail: ['owner', 'super_admin', 'store_manager', 'staff', 'part_time'],
    create: ['owner', 'super_admin', 'store_manager', 'staff'],
    edit: ['owner', 'super_admin', 'store_manager', 'staff'],
    delete: ['owner', 'super_admin', 'store_manager'],
  },

  // ==================== 類別管理 ====================
  categories: {
    view_menu: ['owner', 'super_admin', 'store_manager', 'staff', 'part_time'],
    view_list: ['owner', 'super_admin', 'store_manager', 'staff', 'part_time'],
    view_detail: ['owner', 'super_admin', 'store_manager', 'staff', 'part_time'],
    create: ['owner', 'super_admin', 'store_manager', 'staff'],
    edit: ['owner', 'super_admin', 'store_manager', 'staff'],
    delete: ['owner', 'super_admin', 'store_manager'],
  },

  // ==================== 品牌管理 ====================
  brands: {
    view_menu: ['owner', 'super_admin', 'store_manager', 'staff', 'part_time'],
    view_list: ['owner', 'super_admin', 'store_manager', 'staff', 'part_time'],
    view_detail: ['owner', 'super_admin', 'store_manager', 'staff', 'part_time'],
    create: ['owner', 'super_admin', 'store_manager', 'staff'],
    edit: ['owner', 'super_admin', 'store_manager', 'staff'],
    delete: ['owner', 'super_admin', 'store_manager'],
  },

  // ==================== 配件管理 ====================
  accessories: {
    view_menu: ['owner', 'super_admin', 'store_manager', 'staff', 'part_time'],
    view_list: ['owner', 'super_admin', 'store_manager', 'staff', 'part_time'],
    view_detail: ['owner', 'super_admin', 'store_manager', 'staff', 'part_time'],
    create: ['owner', 'super_admin', 'store_manager', 'staff'],
    edit: ['owner', 'super_admin', 'store_manager', 'staff'],
    delete: ['owner', 'super_admin', 'store_manager'],
  },

  // ==================== 據點管理 ====================
  locations: {
    view_menu: ['owner', 'super_admin', 'store_manager'],
    view_list: ['owner', 'super_admin', 'store_manager'],
    view_detail: ['owner', 'super_admin', 'store_manager'],
    create: ['owner', 'super_admin'],
    edit: ['owner', 'super_admin', 'store_manager'],
    delete: ['owner', 'super_admin'],
  },

  // ==================== 車輛管理 ====================
  vehicles: {
    view_menu: ['owner', 'super_admin', 'store_manager'],
    view_list: ['owner', 'super_admin', 'store_manager'],
    view_detail: ['owner', 'super_admin', 'store_manager'],
    create: ['owner', 'super_admin', 'store_manager'],
    edit: ['owner', 'super_admin', 'store_manager'],
    delete: ['owner', 'super_admin', 'store_manager'],
  },

  // ==================== Urent 管理 ====================
  // 金融操作：process（日常處理）→ approve（核准）→ finalize（最終簽核）
  urent: {
    view_menu: ['owner', 'super_admin', 'store_manager'],
    view_list: ['owner', 'super_admin', 'store_manager'],
    view_detail: ['owner', 'super_admin', 'store_manager'],
    create: ['owner', 'super_admin', 'store_manager'],
    edit: ['owner', 'super_admin', 'store_manager'],
    delete: ['owner', 'super_admin'],
    process: ['owner', 'super_admin', 'store_manager'],  // 日常處理
    approve: ['owner', 'super_admin'],                    // 提領核准
    finalize: ['owner', 'super_admin'],                   // 最終簽核
    override: ['owner'],                                  // 異常修正（僅 owner）
  },

  // ==================== Urent 明細 ====================
  urent_ledger: {
    view_menu: ['owner', 'super_admin', 'store_manager'],
    view_list: ['owner', 'super_admin', 'store_manager'],
    view_detail: ['owner', 'super_admin', 'store_manager'],
    export: ['owner', 'super_admin'],  // 匯出改為僅 owner + super_admin
  },

  // ==================== Urent 合約 ====================
  urent_contracts: {
    view_menu: ['owner', 'super_admin', 'store_manager'],
    view_list: ['owner', 'super_admin', 'store_manager'],
    view_detail: ['owner', 'super_admin', 'store_manager'],
    create: ['owner', 'super_admin'],
    edit: ['owner', 'super_admin'],
    delete: ['owner', 'super_admin'],
  },

  // ==================== 儲值管理 ====================
  // 金融操作：process（日常處理）→ approve（核准）→ finalize（最終簽核）
  prepaid: {
    view_menu: ['owner', 'super_admin', 'store_manager'],
    view_list: ['owner', 'super_admin', 'store_manager'],
    view_detail: ['owner', 'super_admin', 'store_manager'],
    create: ['owner', 'super_admin', 'store_manager'],
    edit: ['owner', 'super_admin', 'store_manager'],
    delete: ['owner', 'super_admin'],
    process: ['owner', 'super_admin', 'store_manager'],  // 日常處理
    approve: ['owner', 'super_admin'],                    // 儲值核准
    finalize: ['owner', 'super_admin'],                   // 最終簽核
  },

  // ==================== 儲值客戶 ====================
  prepaid_customers: {
    view_menu: ['owner', 'super_admin', 'store_manager'],
    view_list: ['owner', 'super_admin', 'store_manager'],
    view_detail: ['owner', 'super_admin', 'store_manager'],
    export: ['owner', 'super_admin'],  // 匯出改為僅 owner + super_admin
  },

  // ==================== 儲值明細 ====================
  prepaid_ledger: {
    view_menu: ['owner', 'super_admin', 'store_manager'],
    view_list: ['owner', 'super_admin', 'store_manager'],
    view_detail: ['owner', 'super_admin', 'store_manager'],
    export: ['owner', 'super_admin'],  // 匯出改為僅 owner + super_admin
  },

  // ==================== 儲值方案 ====================
  prepaid_plans: {
    view_menu: ['owner', 'super_admin', 'store_manager'],
    view_list: ['owner', 'super_admin', 'store_manager'],
    view_detail: ['owner', 'super_admin', 'store_manager'],
    create: ['owner', 'super_admin'],
    edit: ['owner', 'super_admin'],
    delete: ['owner', 'super_admin'],
  },

  // ==================== 業績報表 ====================
  analytics: {
    view_menu: ['owner', 'super_admin', 'store_manager'],
    view_list: ['owner', 'super_admin', 'store_manager'],
    view_detail: ['owner', 'super_admin', 'store_manager'],
    export: ['owner', 'super_admin'],  // 匯出改為僅 owner + super_admin
  },

  // ==================== 合約管理（法務文件）====================
  // owner: 系統治理 + 法務管理
  // super_admin: 僅可查看
  // store_manager: 不可查看
  contracts: {
    view_menu: ['owner', 'super_admin'],       // 移除 store_manager
    view_list: ['owner', 'super_admin'],       // 移除 store_manager
    view_detail: ['owner', 'super_admin'],     // 移除 store_manager
    create: ['owner'],                         // 僅 owner
    edit: ['owner'],                           // 僅 owner
    delete: ['owner'],                         // 僅 owner
    manage_legal: ['owner'],                   // 法務管理僅 owner
  },

  // ==================== 表單管理 ====================
  forms: {
    view_menu: ['owner', 'super_admin', 'store_manager'],
    view_list: ['owner', 'super_admin', 'store_manager'],
    view_detail: ['owner', 'super_admin', 'store_manager'],
    export: ['owner', 'super_admin', 'store_manager'],
  },

  // ==================== CMS ====================
  cms: {
    view_menu: ['owner', 'super_admin', 'store_manager'],
    view_list: ['owner', 'super_admin', 'store_manager'],
    view_detail: ['owner', 'super_admin', 'store_manager'],
    create: ['owner', 'super_admin', 'store_manager'],
    edit: ['owner', 'super_admin', 'store_manager'],
    delete: ['owner', 'super_admin'],
  },

  // ==================== 管理員管理（系統治理）====================
  // owner: 完整管理權限
  // super_admin: 可查看、邀請、編輯（同級或下級）
  // store_manager: 可查看、邀請（下級）
  admins: {
    view_menu:   ['owner', 'super_admin', 'store_manager'],
    view_list:   ['owner', 'super_admin', 'store_manager'],
    view_detail: ['owner', 'super_admin', 'store_manager'],
    create:      ['owner', 'super_admin', 'store_manager'], // 邀請管理員
    edit:        ['owner', 'super_admin'],                  // 編輯管理員（store_manager 僅能透過 invite 新增）
    delete:      ['owner'],                                 // 刪除僅 owner
    manage_system: ['owner'],                              // 系統治理（下載等）僅 owner
  },

  // ==================== 出勤管理 ====================
  attendance: {
    view_menu:   ['owner', 'super_admin', 'store_manager'],
    view_list:   ['owner', 'super_admin', 'store_manager'],
    view_detail: ['owner', 'super_admin', 'store_manager'],
    create:      ['owner', 'super_admin', 'store_manager'],  // 新增補卡
    edit:        ['owner', 'super_admin', 'store_manager'],  // 修正補卡
    override:    ['owner', 'super_admin'],                   // 主管自補（高風險）
    export:      ['owner', 'super_admin'],
  },

  // ==================== 系統設定（系統治理）====================
  // 僅 owner 可管理系統設定
  settings: {
    view_menu: ['owner'],      // 僅 owner
    view_list: ['owner'],      // 僅 owner
    manage_system: ['owner'],  // 僅 owner
  },

  // ==================== Google Calendar（API 串接）====================
  // 僅 owner 可管理 API 串接
  google_calendar: {
    view_menu: ['owner'],      // 僅 owner
    view_list: ['owner'],      // 僅 owner
    view_detail: ['owner'],    // 僅 owner
    manage_system: ['owner'],  // 僅 owner
  },

  // ==================== LINE Notify（API 串接）====================
  // 僅 owner 可管理 API 串接
  line_notify: {
    view_menu: ['owner'],      // 僅 owner
    view_list: ['owner'],      // 僅 owner
    view_detail: ['owner'],    // 僅 owner
    manage_system: ['owner'],  // 僅 owner
  },

  // ==================== 里程碑管理 ====================
  milestones: {
    view_menu: ['owner', 'super_admin', 'store_manager'],
    view_list: ['owner', 'super_admin', 'store_manager'],
    view_detail: ['owner', 'super_admin', 'store_manager'],
    create: ['owner', 'super_admin', 'store_manager'],
    edit: ['owner', 'super_admin', 'store_manager'],
    delete: ['owner', 'super_admin'],
  },

  // ==================== 案例影片管理 ====================
  service_cases: {
    view_menu: ['owner', 'super_admin', 'store_manager'],
    view_list: ['owner', 'super_admin', 'store_manager'],
    view_detail: ['owner', 'super_admin', 'store_manager'],
    create: ['owner', 'super_admin', 'store_manager'],
    edit: ['owner', 'super_admin', 'store_manager'],
    delete: ['owner', 'super_admin'],
  },

  // ==================== API 設定（API 串接）====================
  // 僅 owner 可管理 API 設定
  api_settings: {
    view_menu: ['owner'],      // 僅 owner
    view_list: ['owner'],      // 僅 owner
    view_detail: ['owner'],    // 僅 owner
    manage_system: ['owner'],  // 僅 owner
  },

  // ==================== 前台商品排序設定（系統治理）====================
  // 僅 owner / super_admin 可查看；僅 owner 可編輯
  storefront_sort: {
    view_menu: ['owner', 'super_admin'],
    view_list: ['owner', 'super_admin'],
    view_detail: ['owner', 'super_admin'],
    manage_system: ['owner'],
  },

  // ==================== AI Agent 管理 ====================
  // 僅 owner 可管理 AI Agent 設定與對話紀錄
  ai_agent: {
    view_menu: ['owner'],      // 僅 owner
    view_list: ['owner'],      // 僅 owner
    view_detail: ['owner'],    // 僅 owner
    create: ['owner'],         // 僅 owner
    edit: ['owner'],           // 僅 owner
    delete: ['owner'],         // 僅 owner
    manage_system: ['owner'],  // 僅 owner
  },

  // ==================== 排班管理 ====================
  schedules: {
    view_menu: ['owner', 'super_admin', 'store_manager'],
    view_list: ['owner', 'super_admin', 'store_manager'],
    view_detail: ['owner', 'super_admin', 'store_manager'],
    create: ['owner', 'super_admin', 'store_manager'],
    edit: ['owner', 'super_admin', 'store_manager'],
    delete: ['owner', 'super_admin'],
    process: ['owner', 'super_admin', 'store_manager'],
    approve: ['owner', 'super_admin'],
    override: ['owner', 'super_admin'],
  },

  // ==================== 甲方資料設定 (合約 & 法務) ====================
  legal_settings: {
    view_menu: ['owner', 'super_admin'],
    view_list: ['owner', 'super_admin'],
    view_detail: ['owner', 'super_admin'],
    edit: ['owner'],
    manage_legal: ['owner'],
  },

  frontend_images: {
    view_menu: ['owner', 'super_admin'],
    view_list: ['owner', 'super_admin'],
    view_detail: ['owner', 'super_admin'],
    create: ['owner', 'super_admin'],
    edit: ['owner', 'super_admin'],
    delete: ['owner', 'super_admin'],
  },

  // ==================== 系統治理資訊（owner only）====================
  // RBAC Canonical Matrix、Security Patch Registry、Readdy Change Control 唯讀頁面
  system_governance: {
    view_menu: ['owner'],
    view_list: ['owner'],
    view_detail: ['owner'],
    manage_system: ['owner'],
  },

  // ==================== MuVi Space 預約管理 ====================
  muvi_bookings: {
    view_menu: ['owner', 'super_admin', 'store_manager'],
    view_list: ['owner', 'super_admin', 'store_manager'],
    view_detail: ['owner', 'super_admin', 'store_manager'],
    edit: ['owner', 'super_admin', 'store_manager'],
    process: ['owner', 'super_admin', 'store_manager'],
  },

  // ==================== MuVi Space 內容管理 ====================
  muvi_content: {
    view_menu: ['owner', 'super_admin', 'store_manager'],
    view_list: ['owner', 'super_admin', 'store_manager'],
    view_detail: ['owner', 'super_admin', 'store_manager'],
    edit: ['owner', 'super_admin', 'store_manager'],
    process: ['owner', 'super_admin', 'store_manager'],
  },

  // ==================== MuVi Space 合作夥伴管理 ====================
  muvi_partners: {
    view_menu: ['owner', 'super_admin', 'store_manager'],
    view_list: ['owner', 'super_admin', 'store_manager'],
    view_detail: ['owner', 'super_admin', 'store_manager'],
    create: ['owner', 'super_admin'],
    edit: ['owner', 'super_admin'],
    delete: ['owner', 'super_admin'],
    process: ['owner', 'super_admin'],
  },
};

/**
 * 檢查角色是否有特定模組的特定操作權限
 * 
 * @param role - 角色
 * @param module - 模組
 * @param action - 操作
 * @returns true 如果有權限
 */
export function hasPermission(
  role: AdminRole,
  module: Module,
  action: Action
): boolean {
  const modulePermissions = permissions[module];
  if (!modulePermissions) return false;

  const allowedRoles = modulePermissions[action];
  if (!allowedRoles) return false;

  return allowedRoles.includes(role);
}

/**
 * 取得角色在特定模組的所有權限
 * 
 * @param role - 角色
 * @param module - 模組
 * @returns 該角色在該模組的所有操作權限
 */
export function getModulePermissions(
  role: AdminRole,
  module: Module
): Action[] {
  const modulePermissions = permissions[module];
  if (!modulePermissions) return [];

  return (Object.keys(modulePermissions) as Action[]).filter((action) =>
    hasPermission(role, module, action)
  );
}

/**
 * 取得角色可見的所有模組
 * 
 * @param role - 角色
 * @returns 該角色可見的所有模組列表
 */
export function getVisibleModules(role: AdminRole): Module[] {
  return (Object.keys(permissions) as Module[]).filter((module) =>
    hasPermission(role, module, 'view_menu')
  );
}

/**
 * 計算角色可見的功能模組數量
 *
 * @param role - 角色
 * @returns 該角色可見的功能模組數量
 */
export function getRoleVisibleModuleCount(role: AdminRole): number {
  return getVisibleModules(role).length;
}
